"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, Fragment } from "react";
import Link from "next/link";
import axios from "axios";
import Loader from "@litonarefin/components/Loader";
// import { Turnstile } from "@marsidev/react-turnstile";
import { getStyledIcon } from "@litonarefin/utils/getStyledIcon";
import { getIcon } from "@litonarefin/utils/icons";
import { useGeneratePassword } from "@litonarefin/utils/generateRandomPassword";
import { getPasswordChecker } from "@litonarefin/utils/passwordChecker";
import { InputErrorText } from "@litonarefin/components/InputErrorText";
import capitalize from "lodash/capitalize";
import { getSourceSite } from "@litonarefin/utils/getSourceSite";
import { handleToastr } from "@litonarefin/utils/handleToastr";
import dynamic from "next/dynamic";
import { MaxWidthWrapper } from "@/components/global";

const AccountSidebar = dynamic(() => import("../../login/components/AccountSidebar"), {
  loading: () => <p>Loading...</p>,
});

const ResetPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [expireKey, setExpireKey] = useState(false);
  const [expireMessage, setExpireMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [userInfo, setUserInfo] = useState({
    user_email: "",
    user_pass: "",
    code: "",
    user_pass_confirm: "",
    remember_me: 0,
  });

  const { newPassword, getNewPassword } = useGeneratePassword();
  const checkPass = getPasswordChecker(userInfo?.user_pass);

  const siteSource = getSourceSite();

  const router = useRouter();
  const searchParams = useSearchParams();
  const isResetLinkSend = searchParams.get("reset-link-sent");
  const resetCode = searchParams.get("code");

  const handleOnchange = (e) => {
    let type = e.target.type;
    let name = e.target.name;
    let value = e.target.value;
    if (type == "checkbox") {
      value = !userInfo.remember_me;
    }
    setUserInfo({ ...userInfo, [name]: value });
  };

  const handleForgotPassword = async (e) => {
    setLoading(true);
    setExpireKey(false);
    setExpireMessage("");

    try {
      let { data } = await axios.post(`/api/auth/reset-password`, {
        ...userInfo,
      });

      if (data?.success) {
        setLoading(false);
        handleToastr("Reset Email sent Successfully!", "success");
        localStorage.setItem(`${siteSource}-rst-mail`, userInfo?.user_email);
        // router.push({ query: { "reset-link-sent": true } });
        router.push("?reset-link-sent=true");
      } else {
        setLoading(false);
        setExpireKey(true);
        setExpireMessage(data?.message);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  // Save Password
  const handleSavePassword = async (e) => {
    if (userInfo?.user_pass === userInfo?.user_pass_confirm) {
      // let code = router?.query?.code;
      let email = localStorage.getItem(`${siteSource}-rst-mail`);
      setLoading(true);
      try {
        let { data } = await axios.post(`/api/auth/set-password`, {
          user_pass: userInfo?.user_pass,
          user_email: email || "",
          code: resetCode,
        });

        if (data?.success) {
          setLoading(false);
          handleToastr(data?.message, "success");
          setShowForm(false);
          setExpireKey(false);
          router.push("/account/login");
        }
      } catch (error) {
        console.warn(error);
      }
    }
  };

  // check valid code
  const checkValidCode = async (email, code) => {
    try {
      let { data } = await axios.post(`/api/auth/validate-code`, {
        user_email: email || "",
        code: code,
      });

      if (data?.success) {
        setShowForm(true);
      } else {
        setExpireKey(true);
        setExpireMessage(data?.message);
      }
    } catch (error) {
      setExpireKey(true);
    }
  };

  useEffect(() => {
    if (!!newPassword) {
      setUserInfo({
        ...userInfo,
        user_pass: newPassword,
        user_pass_confirm: newPassword,
      });
    }
  }, [newPassword]);

  useEffect(() => {
    let email = localStorage.getItem(`${siteSource}-rst-mail`) || "";
    let code = resetCode || 0;
    if (email && code) {
      checkValidCode(email, code);
    }
  }, [router]);

  return (
    <>
      <title>Reset Password</title>
      <MaxWidthWrapper className="jlt-mb-32 jlt-pt-[204px]">
        <div className="jlt-grid jlt-grid-cols-1 sm:jlt-grid-cols-2  jlt-gap-8">
          <div className="jlt-p-8 jlt-bg-muted jlt-shadow-[0px_0px_35px_rgba(173,173,188,0.1)] jlt-rounded-lg">
            {/* Reset From */}

            {expireKey && (
              <div className="jlt-bg-red-200 jlt-p-3 jlt-rounded jlt-text-base jlt-text-accent jlt-mb-4 jlt-font-medium jlt-text-center">
                {expireMessage}
              </div>
            )}

            {/* Sent email notice */}
            {!!isResetLinkSend ? (
              <Fragment>
                <div className="jlt-bg-[#E5EEFF] jlt-p-5 jlt-rounded jlt-text-center jlt-mb-4">
                  <h2 className="jlt-text-xl jlt-text-[#16213E] jlt-font-bold jlt-text-center">
                    Check Email
                  </h2>
                  <div className="jlt-mt-6">
                    <p className="jlt-text-[#475467]">
                      We’ve sent you an email with a link to reset your password. Follow the
                      instructions to create a new password.
                      {/* <Link
                                                    href="/account/login"
                                                    className="jlt-text-[--primary]"
                                                >
                                                    Please login here.
                                                </Link> */}
                    </p>
                  </div>
                </div>
              </Fragment>
            ) : null}

            {/* Reset From */}
            {!isResetLinkSend && !showForm ? (
              <Fragment>
                <h5 className="jlt-text-foreground jlt-text-center">Reset Password</h5>
                <div className="jlt-mt-12">
                  <form>
                    <div>
                      <label
                        htmlFor="emailAddress"
                        className="jlt-text-ring jlt-font-semibold jlt-leading-6"
                      >
                        Username or Email*
                      </label>
                      <input
                        required
                        type="text"
                        name="user_email"
                        onChange={handleOnchange}
                        value={userInfo.user_email}
                        className="jlt-w-full jlt-mt-2 jlt-px-4 jlt-py-3 jlt-bg-background jlt-rounded jlt-border jlt-border-border jlt-outline-none"
                        id="emailAddress"
                        placeholder="Username or Email"
                      />
                    </div>
                    <div className="jlt-my-4">
                      {/* <Turnstile
                                                    siteKey={
                                                        process.env
                                                            .NEXT_PUBLIC_CLOUDFLARE_TRUNSTILE_SITE_KEY
                                                    }
                                                /> */}
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={userInfo?.user_email ? false : true}
                        className="jlt-inline-flex jlt-gap-1 jlt-items-center jlt-justify-center jlt-whitespace-nowrap jlt-rounded-md jlt-text-sm jlt-font-medium jlt-ring-offset-background focus-visible:jlt-outline-none focus-visible:jlt-ring-2 focus-visible:jlt-ring-ring focus-visible:jlt-ring-offset-2 disabled:jlt-pointer-events-none disabled:jlt-opacity-50 active:jlt-scale-95 jlt-transition-all jlt-bg-primary jlt-text-white hover:jlt-bg-primary/90 jlt-primary jlt-h-8 jlt-px-3"
                      >
                        {loading && (
                          <Loader
                            styles={{
                              width: "22px",
                              height: "22px",
                              borderColor: "hsl(var(--border))",
                            }}
                            type="button-loader"
                          />
                        )}
                        Get new Password
                        {getStyledIcon(getIcon("largeArrow"), "#fff")}
                      </button>
                      <hr className="jlt-w-full jlt-mt-10 jlt-mb-5 jlt-border jlt-border-[rgb(69_69_69);]" />
                      <p className="jlt-text-center jlt-text-base jlt-font-bold jlt-text-[#72798A]">
                        <Link href="/account/login" className="jlt-text-link jlt-cursor-pointer">
                          Login
                        </Link>
                        &nbsp;|&nbsp;
                        <Link href="/account/register" className="jlt-text-link jlt-cursor-pointer">
                          Register Now
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>
              </Fragment>
            ) : null}

            {/* Set password reset form */}

            {showForm ? (
              <Fragment>
                <h5 className="jlt-text-foreground jlt-text-center">Reset New Password</h5>
                <div className="jlt-mt-12">
                  <form>
                    <div>
                      <div>
                        <div className="jlt-flex jlt-items-center jlt-justify-between jlt-mt-2">
                          <label
                            htmlFor="user_pass"
                            className="jlt-text-ring jlt-font-semibold jlt-leading-6"
                          >
                            New password*
                          </label>
                          <span
                            onClick={() => getNewPassword()}
                            className="jlt-cursor-pointer jlt-text-ring jlt-text-sm jlt-font-medium"
                          >
                            Generate Password!
                          </span>
                        </div>
                      </div>

                      <div className="jlt-relative jlt-mt-3">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="user_pass"
                          onChange={handleOnchange}
                          value={userInfo.user_pass}
                          className="jlt-w-full jlt-px-4 jlt-py-3 jlt-bg-background jlt-rounded jlt-border jlt-border-border jlt-outline-none"
                          id="user_pass"
                        />
                        <div
                          onClick={() => setShowPassword(!showPassword)}
                          className="jlt-absolute jlt-top-[18px] jlt-right-[18px] jlt-cursor-pointer jlt-flex jlt-items-center jlt-justify-center"
                        >
                          {showPassword
                            ? getStyledIcon(getIcon("openEye"), "#475467", "stroke")
                            : getStyledIcon(getIcon("closeEye"), "#475467", "stroke")}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="jlt-mt-2 jlt-flex jlt-items-center jlt-gap-2">
                        <div className="jlt-relative jlt-w-full jlt-h-[8px] jlt-bg-background jlt-rounded-full jlt-overflow-hidden">
                          <div
                            className={`jlt-absolute jlt-h-full jlt-transition-all jlt-duration-400 ${
                              checkPass === "poor"
                                ? "jlt-bg-[#EF6820] jlt-w-full"
                                : checkPass === "weak"
                                ? "jlt-bg-[#EF6820] jlt-w-full"
                                : checkPass === "strong"
                                ? "jlt-bg-[#EF6820] jlt-w-full"
                                : "jlt-w-0"
                            }`}
                          />
                        </div>
                        <div className="jlt-relative jlt-w-full jlt-h-[8px] jlt-bg-background jlt-rounded-full jlt-overflow-hidden">
                          <div
                            className={`jlt-absolute jlt-h-full jlt-transition-all jlt-duration-400 ${
                              checkPass === "poor"
                                ? " jlt-w-0"
                                : checkPass === "weak"
                                ? "jlt-bg-[#EAAA08] jlt-w-full"
                                : checkPass === "strong"
                                ? "jlt-bg-[#EAAA08] jlt-w-full"
                                : "jlt-w-0"
                            }`}
                          />
                        </div>
                        <div className="jlt-relative jlt-w-full jlt-h-[8px] jlt-bg-background jlt-rounded-full jlt-overflow-hidden">
                          <div
                            className={`jlt-absolute jlt-h-full jlt-transition-all jlt-duration-400 ${
                              checkPass === "poor"
                                ? "jlt-w-0"
                                : checkPass === "weak"
                                ? "jlt-w-0"
                                : checkPass === "strong"
                                ? "jlt-bg-[#16B364] jlt-w-full"
                                : "jlt-w-0"
                            }`}
                          />
                        </div>
                      </div>
                      <span
                        className={`jlt-text-xs jlt-italic jlt-font-medium jlt-text-ring ${
                          checkPass ? "jlt-visible" : "jlt-invisible"
                        }`}
                      >
                        {capitalize(checkPass) || "Text"}
                      </span>
                    </div>

                    <div className="jlt-mt-4">
                      <label
                        htmlFor="user_pass_confirm"
                        className="jlt-text-ring jlt-font-semibold jlt-leading-6"
                      >
                        Re-enter new password*
                      </label>

                      <div className="jlt-relative jlt-mt-2">
                        <input
                          type={showRePassword ? "text" : "password"}
                          name="user_pass_confirm"
                          onChange={handleOnchange}
                          value={userInfo.user_pass_confirm}
                          className="jlt-w-full jlt-px-4 jlt-py-3 jlt-bg-background jlt-rounded jlt-border jlt-border-border jlt-outline-none"
                          id="user_pass_confirm"
                        />
                        <div
                          onClick={() => setShowRePassword(!showRePassword)}
                          className="jlt-absolute jlt-top-[18px] jlt-right-[18px] jlt-cursor-pointer jlt-flex jlt-items-center jlt-justify-center"
                        >
                          {showRePassword
                            ? getStyledIcon(getIcon("openEye"), "#475467", "stroke")
                            : getStyledIcon(getIcon("closeEye"), "#475467", "stroke")}
                        </div>
                      </div>
                    </div>

                    {userInfo?.user_pass !== userInfo.user_pass_confirm &&
                    !!userInfo.user_pass_confirm ? (
                      <InputErrorText text="Password doesn't match." />
                    ) : null}

                    <div className="jlt-my-4">
                      {/* <Turnstile
                                                    siteKey={
                                                        process.env
                                                            .NEXT_PUBLIC_CLOUDFLARE_TRUNSTILE_SITE_KEY
                                                    }
                                                /> */}
                    </div>

                    <div>
                      <button
                        disabled={checkPass === "strong" ? false : true}
                        type="button"
                        onClick={handleSavePassword}
                        className="jlt-inline-flex jlt-gap-1 jlt-items-center jlt-justify-center jlt-whitespace-nowrap jlt-rounded-md jlt-text-sm jlt-font-medium jlt-ring-offset-background focus-visible:jlt-outline-none focus-visible:jlt-ring-2 focus-visible:jlt-ring-ring focus-visible:jlt-ring-offset-2 disabled:jlt-pointer-events-none disabled:jlt-opacity-50 active:jlt-scale-95 jlt-transition-all jlt-bg-primary jlt-text-white hover:jlt-bg-primary/90 jlt-primary jlt-h-8 jlt-px-3"
                      >
                        Save
                        {loading && (
                          <Loader
                            styles={{
                              width: "22px",
                              height: "22px",
                              borderColor: "hsl(var(--border))",
                            }}
                            type="button-loader"
                          />
                        )}
                        {getStyledIcon(getIcon("largeArrow"), "#fff")}
                      </button>
                      <hr className="jlt-w-full jlt-h-1 jlt-mt-10 jlt-mb-5 jlt-border-[rgb(69_69_69)]" />
                      <p className="jlt-text-center jlt-text-base jlt-font-bold jlt-text-[#72798A]">
                        <Link href="/account/login" className="jlt-text-link jlt-cursor-pointer">
                          Login
                        </Link>
                        &nbsp;|&nbsp;
                        <Link href="/account/register" className="jlt-text-link jlt-cursor-pointer">
                          Register Now
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>
              </Fragment>
            ) : null}
          </div>
          <AccountSidebar />
        </div>
      </MaxWidthWrapper>
    </>
  );
};

export default ResetPage;
